package com.evoapps.evoshift.data.dto.workplace

import kotlinx.serialization.Serializable

@Serializable
data class WorkplaceDto(
    val id: String,
    val ownerId: String,
    val name: String,
    val address: String,
    /** Legacy "lat, lng" text, still written for apps before SH-110 (see resolveStoredLocation). */
    val geoplace: String? = null,
    val settings: WorkplaceSettingsDto,
    /** SH-110: the point as numbers; SH-106's clock-in radius is measured from here. */
    val location: GeoLocationDto? = null,
    /** SH-267: the workplace group (functions/workplaceGroups.js writes it; the rules keep clients off). */
    val groupId: String? = null
)

/** `workplaces/{id}.location`: decimal degrees (WGS 84). */
@Serializable
data class GeoLocationDto(
    val latitude: Double,
    val longitude: Double
)

@Serializable
data class WorkplaceSettingsDto(
    val timezone: String,
    val currency: String,
    val firstDayOfWeek: Int,
    val availableRoles: List<String>,
    val workplaceSkills: List<String>,
    val shiftSwapPolicy: String,
    val defaultWeeklySkeleton: Map<Int, String> = emptyMap(),
    val definedAvailabilitySlots: List<AvailabilitySlotDto> = emptyList(),
    val market: String = "POLAND",
    val payroll: WorkplacePayrollDto = WorkplacePayrollDto(),
    /** Read by functions/noShow.js (SH-73); 0 = off. */
    val noShowAlertMinutes: Int = 15,
    /** Read by functions/checklists.js (SH-121); 0 = off. */
    val checklistAlertMinutes: Int = 30,
    /** SH-47: refuse assigning someone with an expired medical exam or OHS training. */
    val blockExpiredStaffChecks: Boolean = false,
    /** SH-133: the 11 h and 35 h rest for every contract (missing: on). */
    val restRulesForAllContracts: Boolean = true,
    /** SH-55: OPEN, TRADING_SUNDAYS or CLOSED; missing means OPEN. */
    val sundayPolicy: String = "OPEN",
    /** SH-104: positions and skills whose holders lead shifts. */
    val leadRoles: List<String> = emptyList(),
    val leadSkills: List<String> = emptyList(),
    /** SH-106: clock-in only near the workplace; missing means off. */
    val geofence: GeofenceDto? = GeofenceDto(),
    /** SH-116: missing means the default (paid break per the law, no unpaid break, no reminder). */
    val breakPolicy: BreakPolicyDto? = null,
    /** SH-134: flag thresholds, payable time from the plan, tablet safeguards; missing means the defaults. */
    val clockReview: ClockReviewSettingsDto? = null,
    /** SH-226: weather fetched by functions/weather.js for the location; missing means off. */
    val weatherEnabled: Boolean = false,
    /** SH-227: school breaks and long weekends on the days; missing means on. */
    val specialDaysEnabled: Boolean = true,
    /** SH-227: the voivodeship the owner chose (Voivodeship's name); missing: read from the address. */
    val voivodeship: String? = null,
    /** SH-223: the request types' numbers; missing means the defaults (Polish law). */
    val requests: RequestSettingsDto? = null,
    /** SH-239: "Made with EVoShift" on the e-mailed rosters; missing means on. */
    val showEvoShiftFooter: Boolean = true,
    /** SH-257: the team enters their own hours (read by functions/workHours.js); missing means off. */
    val workHours: WorkHoursSettingsDto? = null,
    /** SH-258: the printed reports' look (`settings.reports.timesheet`); missing means the defaults. */
    val reports: ReportSettingsDto? = null,
    /**
     * SH-232: when each person works without pay, by user id (UnpaidWork). Outside `payroll`:
     * older app versions write `settings.payroll` whole and would drop it. Written only by
     * WorkplaceRepositoryImpl.updateUnpaidWork (and functions/unpaidWork.js).
     */
    val unpaidPeriods: Map<String, List<UnpaidPeriodDto>> = emptyMap(),
    /** SH-232: the day `payroll.unpaidUserIds` last agreed with [unpaidPeriods] ("YYYY-MM-DD"). */
    val unpaidPeriodsAsOf: String? = null,
    /** SH-255: FULL, WORKING_WITH_ME or OWN_ONLY; missing means FULL. */
    val rosterVisibility: String = "FULL",
    /** SH-255: someone is hidden in the roster for the others (functions/rosterPrivacy.js writes it). */
    val rosterHasHiddenPeople: Boolean = false
)

/** `settings.reports` (SH-258). */
@Serializable
data class ReportSettingsDto(
    val timesheet: TimesheetReportSettingsDto? = null
)

/**
 * `settings.reports.timesheet` (SH-258): hoursSource PLAN, ACTUAL or COMBINED; roundingMinutes
 * 0, 5, 10, 15 or 30; roundingMode NEAREST, START_UP_END_DOWN or START_DOWN_END_UP; weekday NONE,
 * SHORT or FULL. Unknown values read as the defaults.
 */
@Serializable
data class TimesheetReportSettingsDto(
    val hoursSource: String = "COMBINED",
    val roundingMinutes: Int = 0,
    val roundingMode: String = "NEAREST",
    val showRemarks: Boolean = true,
    val showManagerSignature: Boolean = false,
    val weekday: String = "SHORT"
)

/** SH-232: one period without pay: the days [from]–[until] (inclusive; null: from the start / open-ended). */
@Serializable
data class UnpaidPeriodDto(val from: String? = null, val until: String? = null)

/** `settings.requests` (SH-223). */
@Serializable
data class RequestSettingsDto(
    val bloodDonationDays: Int = 2,
    val childcareDays: Int = 2,
    val childcareHours: Int = 16,
    val forceMajeureDays: Int = 2,
    val forceMajeureHours: Int = 16,
    val carerLeaveDays: Int = 5,
    val careAllowanceChildDays: Int = 60,
    val careAllowanceFamilyDays: Int = 14,
    val careAllowanceTotalDays: Int = 60,
    /** Each type's rule by its name; a type not listed has the default rule. */
    val types: Map<String, RequestTypeRuleDto> = emptyMap(),
    /** RequestDefaults name; missing: LEGACY (as a workplace without settings.requests). */
    val defaults: String? = null
)

/**
 * `settings.requests.types.<NAME>`: switched on or off; the contracts (SystemContractType names;
 * null: all) and the owner's own contract types (`owners/{uid}/contract_types` ids; apps before
 * this field skip it).
 */
@Serializable
data class RequestTypeRuleDto(
    val enabled: Boolean = true,
    val contracts: List<String>? = null,
    val contractTypeIds: List<String>? = null
)

/** SH-257: `settings.workHours` (read by functions/workHours.js). */
@Serializable
data class WorkHoursSettingsDto(val selfEntry: Boolean = false)

/** `settings.clockReview` (read by functions/clockReview.js and the clock functions). */
@Serializable
data class ClockReviewSettingsDto(
    val earlyInMinutes: Int = 15,
    val lateOutMinutes: Int = 15,
    val maxOverrunMinutes: Int = 30,
    val veryShortMinutes: Int = 15,
    val countFromPlannedStart: Boolean = false,
    val kioskQrOnly: Boolean = false,
    val kioskPhoto: Boolean = false,
    val sameTabletSeconds: Int = 60,
    val flagNoAppActivity: Boolean = false
)

/** `settings.breakPolicy` (read by functions/breaks.js and the clock functions). */
@Serializable
data class BreakPolicyDto(
    val paidBreak: Boolean = true,
    val unpaidBreakMinutes: Int = 0,
    val reminderAfterHours: Int = 0
)

/** `settings.geofence`: mode OFF, WARN or BLOCK and the radius in metres (read by functions/geofence.js). */
@Serializable
data class GeofenceDto(
    val mode: String = "OFF",
    val radiusMeters: Int = 150
)

@Serializable
data class WorkplacePayrollDto(
    val accidentInsuranceRate: Double = 0.0167,
    val nightStart: String = "22:00",
    val nightEnd: String = "06:00",
    val ownerShiftsUnpaid: Boolean = false,
    /** SH-57: the settlement period in months (1–4); missing means 1. */
    val settlementPeriodMonths: Int = 1,
    /** SH-57: overtime given back as time off instead of the supplements. */
    val overtimeTimeOffInLieu: Boolean = false,
    /** SH-147: people confirmed as working here without pay (user ids). */
    val unpaidUserIds: List<String> = emptyList(),
    /** SH-193: AUTOMATIC or FIXED (OpenShiftCostMode); missing means AUTOMATIC. */
    val openShiftCostMode: String = "AUTOMATIC",
    /** SH-193: the employer cost per hour unassigned shifts are estimated at (fixed, or the automatic one's fallback). */
    val openShiftCostPerHour: Double? = null
)

@Serializable
data class AvailabilitySlotDto(
    val id: String,
    val label: String,
    val startTime: String,
    val endTime: String
)

@Serializable
data class ShiftTemplateDto(
    val id: String,
    val name: String,
    val startTime: String,
    val endTime: String,
    val role: String?,
    val skills: List<String>
)

@Serializable
data class DayTemplateDto(
    val id: String,
    val name: String,
    val shiftTemplateIds: List<String>
)
